import HighlightedItem from '@pelckmans/elementis/components/highlighted-item';
import { PillGroupProps } from '@pelckmans/elementis/components/pill-group';
import { calculateResults } from '@pelckmans/elementis/components/results/utils';
import { formatAbsoluteScore } from '@pelckmans/elementis/components/results';
import { ComponentProps } from 'react';
import { Medialink } from '../../../../../api/medialink';
import ScorableProgress from '../../../../../pages/material/toc/components/ScorableProgress';
import { getResultPercentages, Status } from '../../../../../utils/results';
import { AdaptivePhase } from '../api/exercises-api';
import { useExerciseManagerContext } from '../contexts/ExerciseManagerContext';

interface Props {
  label: string;
  medialink: Medialink;
  resultTitle: string;
  percentageTitle: string;
  scoreTitle: string;
  tags: Omit<PillGroupProps, 'size'>;
  status?: Exclude<Status, 'not-started' | 'not-finished' | 'pending-grading'>;
  phases?: AdaptivePhase[];
  isLastPhase?: boolean;
  score?: {
    maximum: number;
    achievable: number;
    achieved: number;
  };
}

function ExerciseHighLightedItem({
  label,
  medialink,
  score,
  resultTitle,
  percentageTitle,
  scoreTitle,
  status,
  tags,
  phases,
  isLastPhase,
}: Props) {
  const resultScore: ComponentProps<typeof ScorableProgress>['score'] = score ? { ...score, status } : undefined;

  const { showScore } = useExerciseManagerContext();

  if (medialink.assessmentQExercise?.isAdaptive && phases) {
    const transformedPhases = phases.map(phase => ({
      ...phase,
      score: getResultPercentages(phase.score),
    }));

    return (
      <HighlightedItem
        variant="exercise"
        label={label}
        exerciseName={medialink.name}
        tags={tags}
        phases={transformedPhases}
        showScore={showScore}
        score={status === 'finished' ? getResultPercentages(score) : undefined}
        isLastPhase={Boolean(isLastPhase)}
      />
    );
  }

  // Temporary 37.1 fix for eduhint tests
  // Should be removed when unachievable is based on the session result, not a medialink property.
  const unachievable = (!!medialink.unachievable && !!medialink.assessmentQExercise) || resultScore?.achieved === -1;

  return (
    <HighlightedItem
      variant="exercise"
      label={label}
      exerciseName={medialink.name}
      tags={tags}
      result={{
        title: resultTitle,
        value: (
          <ScorableProgress unachievable={unachievable} score={resultScore} responsive={false} fluid showTooltip />
        ),
      }}
      percentage={
        unachievable || !resultScore
          ? undefined
          : {
              title: percentageTitle,
              // reuse calculateResults so the score is calculated the same way as in the ScorableProgress component
              value: calculateResults(getResultPercentages(resultScore)).success,
            }
      }
      score={
        showScore && !unachievable && !!resultScore
          ? {
              title: scoreTitle,
              value: formatAbsoluteScore(resultScore),
            }
          : undefined
      }
    />
  );
}

export default ExerciseHighLightedItem;
